import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import type { LoginResponse, OnlineStatus, SignupResponse, UserDTO } from '@bruno/shared';
import { isTwoFactorChallenge } from '@bruno/shared';
import * as authApi from '../api/auth';
import { setAccessToken } from '../api/client';
import { setMyPresence } from '../api/team';

interface AuthCtx {
  user: UserDTO | null;
  loading: boolean;
  /** Returns the raw LoginResponse so the caller can branch on a 2FA
   *  challenge. When the result is a regular AuthResponse, `user`
   *  state is already updated by the time this resolves. */
  login: (email: string, password: string, captchaToken?: string) => Promise<LoginResponse>;
  /** Finalises a 2FA-gated login: sets the in-memory user after the
   *  caller has exchanged the challenge ticket. */
  setSignedInUser: (user: UserDTO) => void;
  /** Re-fetches the current user from /v1/auth/me and refreshes the
   *  in-memory copy. Used after self-service changes (e.g. enabling 2FA)
   *  so derived UI like the 2FA nudge banner updates without a reload. */
  refreshUser: () => Promise<void>;
  /** Creates the account and triggers a confirmation email. Does NOT sign
   *  the user in — they must confirm their email first. Returns the
   *  verification-required result so the caller can show "check your inbox". */
  signup: (
    email: string,
    password: string,
    name?: string,
    captchaToken?: string,
  ) => Promise<SignupResponse>;
  logout: () => Promise<void>;
  setStatus: (status: OnlineStatus) => Promise<void>;
}

const AuthContext = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserDTO | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const impersonationToken = new URLSearchParams(window.location.search).get('impersonate_token');
      if (impersonationToken) {
        try {
          setAccessToken(impersonationToken);
          const { user: impersonatedUser } = await authApi.getMe();
          const url = new URL(window.location.href);
          url.searchParams.delete('impersonate_token');
          window.history.replaceState({}, '', url.toString());
          if (!cancelled) setUser(impersonatedUser);
        } catch {
          setAccessToken(null);
          if (!cancelled) setUser(null);
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }

      const u = await authApi.tryRestoreSession();
      if (!cancelled) {
        setUser(u);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(
    async (email: string, password: string, captchaToken?: string) => {
      const res = await authApi.login({ email, password, captchaToken });
      if (!isTwoFactorChallenge(res)) setUser(res.user);
      return res;
    },
    [],
  );

  const setSignedInUser = useCallback((u: UserDTO) => {
    setUser(u);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { user: fresh } = await authApi.getMe();
      setUser(fresh);
    } catch {
      // Non-fatal: a transient /me failure shouldn't blow away the
      // session. The next navigation or refresh will reconcile.
    }
  }, []);

  const signup = useCallback(
    async (email: string, password: string, name?: string, captchaToken?: string) => {
      // No session is issued at signup; the caller surfaces a
      // "confirm your email" screen and the user signs in after verifying.
      return authApi.signup({ email, password, name, captchaToken });
    },
    [],
  );

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
    setAccessToken(null);
  }, []);

  const setStatus = useCallback(async (status: OnlineStatus) => {
    await setMyPresence(status);
    setUser((prev) => (prev ? { ...prev, onlineStatus: status } : prev));
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, loading, login, signup, logout, setStatus, setSignedInUser, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthCtx {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
